
import { supabase } from "@/integrations/supabase/client";

// Hide a technique
export const hideTechnique = async (techniqueId: string): Promise<void> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error('You must be logged in to hide a technique');
    }

    const { error } = await supabase
      .from('hidden_techniques')
      .insert({
        technique_id: techniqueId,
        user_id: userData.user.id
      });
      
    if (error) {
      console.error('Error hiding technique:', error);
      throw new Error(error.message);
    }
  } catch (error: unknown) {
    console.error('Unexpected error in hideTechnique:', error);
    throw error instanceof Error
      ? error
      : new Error('An unexpected error occurred while hiding the technique');
  }
};

// Unhide a technique
export const unhideTechnique = async (techniqueId: string): Promise<void> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error('You must be logged in to unhide a technique');
    }

    const { error } = await supabase
      .from('hidden_techniques')
      .delete()
      .eq('technique_id', techniqueId)
      .eq('user_id', userData.user.id);
      
    if (error) {
      console.error('Error unhiding technique:', error);
      throw new Error(error.message);
    }
  } catch (error: unknown) {
    console.error('Unexpected error in unhideTechnique:', error);
    throw error instanceof Error
      ? error
      : new Error('An unexpected error occurred while unhiding the technique');
  }
};

// Check if a technique is hidden
export const checkIfTechniqueIsHidden = async (techniqueId: string): Promise<boolean> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      return false;
    }

    const { data, error } = await supabase
      .from('hidden_techniques')
      .select('id')
      .eq('technique_id', techniqueId)
      .eq('user_id', userData.user.id)
      .maybeSingle();
      
    if (error) {
      console.error('Error checking hidden technique:', error);
      return false;
    }
    
    return !!data;
  } catch (error: unknown) {
    console.error('Unexpected error in checkIfTechniqueIsHidden:', error);
    return false;
  }
};

// Get all hidden technique IDs for current user
export const getHiddenTechniqueIds = async (): Promise<string[]> => {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      return [];
    }

    const { data, error } = await supabase
      .from('hidden_techniques')
      .select('technique_id')
      .eq('user_id', userData.user.id);
      
    if (error) {
      console.error('Error getting hidden techniques:', error);
      return [];
    }
    
    return data.map((item: { technique_id: string }) => item.technique_id);
  } catch (error: unknown) {
    console.error('Unexpected error in getHiddenTechniqueIds:', error);
    return [];
  }
};
